/**
 * SWR cache layer: dual-layer (in-memory Map + localStorage) with TTL config.
 * No React dependencies — pure utility module.
 */

const STORAGE_PREFIX = "ss_cache_";
const MAX_ENTRIES = 50;
const DEFAULT_TTL = 60_000; // 1 minute

interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  ttl: number;
}

// ── TTL Configuration (first match wins) ──

const TTL_RULES: [RegExp, number][] = [
  [/^\/reports\//, 0],
  [/^\/admin\//, 0],
  [/^\/dashboard\/logs/, 0],
  [/^\/api-keys$/, 30_000],
  [/^\/shops$/, 30_000],
  [/^\/dashboard\/stats$/, 300_000],
  [/^\/store-detail\/[^/]+$/, 60_000],
  [/^\/store-detail\/[^/]+\/snapshots/, 120_000],
  [/^\/store-detail\/[^/]+\/changes/, 120_000],
  [/^\/store-detail\/[^/]+\/analytics$/, 600_000],
  [/^\/store-detail\/[^/]+\/inventory\/events/, 120_000],
  [/^\/store-detail\/[^/]+\/inventory\/product\//, 300_000],
  [/^\/store-detail\/[^/]+\/inventory$/, 300_000],
  [/^\/store-detail\/[^/]+\/compare/, 600_000],
  [/^\/stores\?/, 300_000],
  [/^\/stores\//, 600_000],
  [/^\/blog/, 600_000],
];

// ── In-memory cache ──

const memoryCache = new Map<string, CacheEntry>();

// ── Helpers ──

function storageKey(key: string): string {
  return STORAGE_PREFIX + key;
}

function evictOldest(count: number) {
  // Evict from both layers, oldest-first
  const entries: [string, number][] = [];
  for (const [k, v] of memoryCache) {
    entries.push([k, v.timestamp]);
  }
  entries.sort((a, b) => a[1] - b[1]);
  const toRemove = entries.slice(0, count);
  for (const [k] of toRemove) {
    memoryCache.delete(k);
    try { localStorage.removeItem(storageKey(k)); } catch {}
  }
}

// ── Exported API ──

export function getTtlForPath(path: string): number {
  for (const [pattern, ttl] of TTL_RULES) {
    if (pattern.test(path)) return ttl;
  }
  return DEFAULT_TTL;
}

export function getCached<T = any>(key: string): { data: T; isStale: boolean } | null {
  const now = Date.now();

  // Check memory first
  const mem = memoryCache.get(key);
  if (mem) {
    return { data: mem.data as T, isStale: now - mem.timestamp >= mem.ttl };
  }

  // Fall back to localStorage
  try {
    const raw = localStorage.getItem(storageKey(key));
    if (!raw) return null;
    const entry: CacheEntry<T> = JSON.parse(raw);
    // Re-hydrate into memory
    memoryCache.set(key, entry);
    return { data: entry.data, isStale: now - entry.timestamp >= entry.ttl };
  } catch {
    // Corrupted — remove it
    try { localStorage.removeItem(storageKey(key)); } catch {}
    return null;
  }
}

export function setCache<T = any>(key: string, data: T, ttlMs?: number) {
  const ttl = ttlMs ?? getTtlForPath(key);
  const entry: CacheEntry<T> = { data, timestamp: Date.now(), ttl };

  // Write to memory
  if (memoryCache.size > MAX_ENTRIES) evictOldest(10);
  memoryCache.set(key, entry);

  // Write to localStorage
  try {
    localStorage.setItem(storageKey(key), JSON.stringify(entry));
  } catch {
    // QuotaExceededError — evict and retry once
    evictOldest(5);
    try {
      localStorage.setItem(storageKey(key), JSON.stringify(entry));
    } catch {
      // Give up on persistence — memory cache still works
    }
  }
}

export function clearCache(key?: string) {
  if (key) {
    memoryCache.delete(key);
    try { localStorage.removeItem(storageKey(key)); } catch {}
  } else {
    // Clear all ss_cache_* entries
    memoryCache.clear();
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k?.startsWith(STORAGE_PREFIX)) toRemove.push(k);
      }
      for (const k of toRemove) localStorage.removeItem(k);
    } catch {}
  }
}

export function clearByPrefix(prefix: string) {
  // Clear memory entries matching prefix
  for (const key of [...memoryCache.keys()]) {
    if (key.startsWith(prefix)) memoryCache.delete(key);
  }
  // Clear localStorage entries matching prefix
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(storageKey(prefix))) toRemove.push(k);
    }
    for (const k of toRemove) localStorage.removeItem(k);
  } catch {}
}

// ── Hydrate on module load ──

function hydrateFromStorage() {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k?.startsWith(STORAGE_PREFIX)) continue;
      try {
        const entry: CacheEntry = JSON.parse(localStorage.getItem(k)!);
        const cacheKey = k.slice(STORAGE_PREFIX.length);
        memoryCache.set(cacheKey, entry);
      } catch {
        // Corrupted entry — remove
        try { localStorage.removeItem(k); } catch {}
      }
    }
  } catch {}
}

hydrateFromStorage();
